"use client";

import { cn } from "@ui/lib";
import { ChevronDown } from "lucide-react";
import { useTranslations } from "next-intl";
import { useState } from "react";

type FaqItem = {
	questionKey: string;
	answerKey: string;
};

export function FaqSection() {
	const t = useTranslations("aiDescribeImage.client.faq");
	const [openIndex, setOpenIndex] = useState<number | null>(null);

	// FAQ items using translation keys
	const items: FaqItem[] = [
		{
			questionKey: "items.accurateAnalysis.question",
			answerKey: "items.accurateAnalysis.answer",
		},
		{
			questionKey: "items.comprehensiveImageToText.question",
			answerKey: "items.comprehensiveImageToText.answer",
		},
		{
			questionKey: "items.multilingualSupport.question",
			answerKey: "items.multilingualSupport.answer",
		},
		{
			questionKey: "items.fastResults.question",
			answerKey: "items.fastResults.answer",
		},
		{
			questionKey: "items.imageDescriptionUse.question",
			answerKey: "items.imageDescriptionUse.answer",
		},
		{
			questionKey: "items.imageAnalysisProcess.question",
			answerKey: "items.imageAnalysisProcess.answer",
		},
		{
			questionKey: "items.imageAnalysisQuality.question",
			answerKey: "items.imageAnalysisQuality.answer",
		},
		{
			questionKey: "items.descriptionAccuracy.question",
			answerKey: "items.descriptionAccuracy.answer",
		},
	];

	// Toggle FAQ item - open clicked item and close others
	const toggleItem = (index: number) => {
		setOpenIndex(openIndex === index ? null : index);
	};

	return (
		<section className={cn("scroll-mt-20 py-12 lg:py-24")} id="faq">
			<div className="max-w-3xl mx-auto">
				<div className="mb-12 lg:mb-16 text-center">
					<h2 className="text-4xl font-bold mb-2 bg-clip-text text-transparent bg-gradient-to-r from-indigo-500 via-purple-500 to-purple-600">
						{t("title")}
					</h2>
				</div>

				<div className="flex flex-col gap-4">
					{items.map((item, i) => (
						<FaqAccordionItem
							key={`faq-item-${i}`}
							item={item}
							index={i}
							isOpen={openIndex === i}
							toggleItem={toggleItem}
						/>
					))}
				</div>
			</div>
		</section>
	);
}

function FaqAccordionItem({
	item,
	index,
	isOpen,
	toggleItem,
}: {
	item: FaqItem;
	index: number;
	isOpen: boolean;
	toggleItem: (index: number) => void;
}) {
	const t = useTranslations("aiDescribeImage.client.faq");

	return (
		<div className="border-b border-gray-200">
			<button
				type="button"
				onClick={() => toggleItem(index)}
				className="flex justify-between items-center w-full py-6 text-left"
				aria-expanded={isOpen}
				aria-controls={`faq-answer-${index}`}
			>
				<h4 className="text-xl font-medium">{t(item.questionKey)}</h4>
				<ChevronDown
					className={cn(
						"h-5 w-5 flex-shrink-0 text-gray-500 transition-transform duration-200",
						isOpen && "transform rotate-180",
					)}
				/>
			</button>
			<div
				id={`faq-answer-${index}`}
				className={cn(
					"overflow-hidden transition-all duration-300",
					isOpen
						? "max-h-[2000px] opacity-100 pb-6"
						: "max-h-0 opacity-0",
				)}
			>
				<div className="text-gray-600 leading-relaxed">
					{t(item.answerKey)}
				</div>
			</div>
		</div>
	);
}
